import React, { Fragment } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Header from "../../components/Header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import SEO from "../../components/SEO/SEO";

import cardBg from "../../images/bg/pricing-bg01.png";
import serviceBg from "../../images/bg/pricing-bg.png";

interface ModuleConfig {
  icon: string;
  name: string;
  aiCharacter: string;
  tagline: string;
  description: string;
  features: string[];
  freeFeatures?: string[];
  paidFeatures?: string[];
  highlight: string;
  badge?: string;
  interconnections?: { module: string; slug: string; description: string }[];
  /**
   * A pointer off the feature pages to a page that documents something this
   * module depends on but does not own — currently the Google permission model
   * behind CHORUS's YouTube channel and the Gmail mailbox in the Email suite.
   * Optional: most modules connect to nothing outside AXOIX and render nothing.
   */
  externalLink?: { to: string; label: string };
}

const MODULE_CONFIG: Record<string, ModuleConfig> = {
  link: {
    icon: "fas fa-phone-volume",
    name: "LINK - Business Phone System",
    aiCharacter: "LINK",
    tagline: "Your Complete Telecom Department",
    description: "Every AXOIX subscription includes a built-in business phone system. No expensive PBX hardware, no complicated setup, no monthly fees for internal calls.",
    features: [],
    freeFeatures: [
      "Internal extensions: 3 / 10 / 15 / unlimited by plan",
      "Browser-based WebRTC calling",
      "Internal voicemail",
      "Call transfers between team members",
      "Presence/availability status",
      "No phone hardware required",
    ],
    paidFeatures: [
      "DID phone numbers (+91-XXX-XXXX)",
      "Professional IVR builder (Press 1 for Sales...)",
      "Call recording & storage",
      "Click-to-call from CRM",
      "Missed call alerts & notifications",
      "Call analytics & reporting",
    ],
    highlight: "FREE internal calling with all paid plans!",
    interconnections: [
      { module: "CRM & Sales", slug: "crm", description: "Click-to-call leads directly from BARTER pipeline" },
      { module: "CONNECT Chat", slug: "connect", description: "Escalate chat conversations to voice calls instantly" },
      { module: "Analytics", slug: "analytics", description: "Call metrics feed into VARN business intelligence dashboards" },
    ],
  },
  accounting: {
    icon: "fas fa-calculator",
    name: "Accounting & Finance",
    aiCharacter: "LEDGER",
    tagline: "Complete Financial Control",
    description: "LEDGER provides enterprise-grade double-entry accounting designed for Indian businesses. From GST compliance to multi-currency support — your finances are in expert hands.",
    features: [
      "Double-entry bookkeeping with chart of accounts",
      "Invoice generation with GST calculations",
      "Expense tracking & categorization",
      "Bank reconciliation",
      "GST return preparation (GSTR-1, GSTR-3B)",
      "TDS calculations & deductions",
      "Multi-currency support",
      "Profit & Loss statements",
      "Balance sheet generation",
      "Cash flow reports",
      "Accounts receivable/payable aging",
      "Budget planning & tracking",
    ],
    highlight: "Indian tax compliance built-in!",
    interconnections: [
      { module: "HR & Workforce", slug: "hr", description: "Payroll runs auto-generate journal entries and statutory filings" },
      { module: "Inventory", slug: "inventory", description: "Purchase orders create payable entries automatically" },
      { module: "Quotations", slug: "quotations", description: "Accepted quotes auto-convert to GST-compliant invoices" },
      { module: "UPI Payments", slug: "upi-payments", description: "Payments received via UPI auto-reconcile with invoices" },
    ],
  },
  hr: {
    icon: "fas fa-users",
    name: "HR & Workforce Management",
    aiCharacter: "KEEPER",
    tagline: "Manage Your Most Valuable Asset",
    description: "KEEPER handles everything HR — from recruitment to retirement. Employee records, attendance, payroll preparation, and performance management in one unified system.",
    features: [
      "Complete employee database",
      "Document management (ID proofs, contracts)",
      "Attendance tracking (biometric integration ready)",
      "Leave management with approval workflows",
      "Payroll preparation & salary slips",
      "Statutory compliance (PF, ESI, PT)",
      "Recruitment pipeline management",
      "Job posting & application tracking",
      "Onboarding checklists",
      "Performance review cycles",
      "Training & certification tracking",
      "Exit management & full-and-final",
    ],
    highlight: "From hiring to retiring — one system!",
    interconnections: [
      { module: "Accounting", slug: "accounting", description: "Payroll processing auto-creates salary journal entries" },
      { module: "CONNECT Chat", slug: "connect", description: "Department channels update when employees join or leave" },
      { module: "LINK Phone", slug: "link", description: "New employees auto-provisioned with phone extensions" },
      { module: "Project Management", slug: "projects", description: "Employee skills matched to project resource allocation" },
    ],
  },
  inventory: {
    icon: "fas fa-boxes",
    name: "Inventory Management",
    aiCharacter: "HAUL",
    tagline: "Never Run Out of Stock",
    description: "HAUL ensures you always have what you need, when you need it. Real-time inventory tracking, automated reorder points, and multi-warehouse support.",
    features: [
      "Real-time stock levels",
      "Multi-warehouse management",
      "Barcode/QR code support",
      "Low-stock alerts & notifications",
      "Automated purchase order generation",
      "Supplier management",
      "Stock transfers between locations",
      "Batch & serial number tracking",
      "Expiry date management",
      "Stock valuation (FIFO, weighted average)",
      "Inventory audit & adjustment",
      "Stock movement reports",
    ],
    highlight: "Multi-warehouse with real-time tracking!",
    interconnections: [
      { module: "Accounting", slug: "accounting", description: "Purchase orders auto-create payable entries in LEDGER" },
      { module: "CRM & Sales", slug: "crm", description: "Stock availability shown in real-time during deal quoting" },
      { module: "AI Costing", slug: "costing", description: "Material costs feed into costing estimates automatically" },
    ],
  },
  projects: {
    icon: "fas fa-project-diagram",
    name: "Project Management",
    aiCharacter: "FORGE",
    tagline: "Deliver Projects On Time",
    description: "FORGE keeps your projects on track with task management, team coordination, and progress tracking. Kanban boards, Gantt charts, and automated reporting.",
    features: [
      "Project creation with milestones",
      "Task assignment & dependencies",
      "Kanban board view",
      "Gantt chart timeline",
      "Time tracking & logging",
      "Resource allocation",
      "Team workload balancing",
      "Progress tracking & reporting",
      "File attachments & comments",
      "Client access portals",
      "Budget tracking per project",
      "Automated progress emails",
    ],
    highlight: "Kanban + Gantt + Time tracking!",
  },
  crm: {
    icon: "fas fa-handshake",
    name: "CRM & Sales",
    aiCharacter: "BARTER",
    tagline: "Close More Deals",
    description: "BARTER manages your entire sales pipeline — from lead capture to deal closure. With click-to-call via LINK, your sales team is unstoppable.",
    features: [
      "Lead capture & management",
      "Lead scoring & prioritization",
      "Contact & company database",
      "Sales pipeline visualization",
      "Opportunity tracking",
      "Follow-up reminders",
      "Email integration",
      "Click-to-call via LINK",
      "Quote & proposal generation",
      "Sales forecasting",
      "Commission tracking",
      "Win/loss analysis",
    ],
    highlight: "Click-to-call with LINK integration!",
    interconnections: [
      { module: "LINK Phone", slug: "link", description: "Click-to-call leads directly from the pipeline" },
      { module: "CHORUS Social", slug: "chorus", description: "Social media leads auto-populate CRM pipeline" },
      { module: "Quotations", slug: "quotations", description: "Deals convert to branded quotes with one click" },
      { module: "SEAL E-Sign", slug: "seal", description: "Quotes sent for e-signature directly from deal view" },
    ],
  },
  email: {
    icon: "fas fa-envelope",
    name: "Email AI System",
    aiCharacter: "ECHO",
    tagline: "Professional Communication Made Easy",
    description: "ECHO powers your email with AI assistance — compose, reply, summarize, and categorize. Self-hosted email with custom domain support, or connect your existing Gmail and work the same mailbox from inside AXOIX.",
    features: [
      "AI-powered email composition",
      "Smart reply suggestions",
      "Email summarization",
      "Automatic categorization",
      "Custom domain email (@yourcompany.com)",
      "Gmail connection — read, send, reply, label, archive and download attachments in the AXOIX inbox",
      "Or send-only Gmail: quotes and invoices leave from your own address, and AXOIX never reads your mail",
      "Multi-user mailboxes",
      "2GB storage per user",
      "Email templates",
      "Signature management",
      "Spam filtering",
      "Email scheduling",
      "Read receipts",
    ],
    highlight: "AI writes your emails in seconds!",
    externalLink: { to: "/feature/google-workspace", label: "See the Google Workspace module" },
  },
  compliance: {
    icon: "fas fa-shield-alt",
    name: "Compliance & Legal",
    aiCharacter: "CIPHER",
    tagline: "Stay on the Right Side",
    description: "CIPHER monitors compliance requirements, tracks document expirations, and maintains audit trails. Never miss a renewal or regulatory deadline.",
    features: [
      "Document expiry tracking",
      "License & registration management",
      "Insurance policy tracking",
      "Regulatory deadline alerts",
      "Audit trail maintenance",
      "Compliance checklist templates",
      "Digital document storage",
      "Version control",
      "Access control & permissions",
      "Compliance reports",
      "Vendor compliance tracking",
      "Custom compliance workflows",
    ],
    highlight: "Never miss a deadline!",
  },
  analytics: {
    icon: "fas fa-chart-line",
    name: "Analytics & Reporting",
    aiCharacter: "VARN",
    tagline: "Data-Driven Decisions",
    description: "VARN turns raw data into actionable insights. Real-time dashboards, custom reports, and trend analysis to help you make smarter business decisions.",
    features: [
      "Real-time dashboard widgets",
      "Custom report builder",
      "Sales analytics",
      "Financial metrics",
      "Inventory turnover reports",
      "Employee performance metrics",
      "Project profitability analysis",
      "Trend identification",
      "Forecasting models",
      "Export to PDF/Excel",
      "Scheduled report delivery",
      "Role-based report access",
    ],
    highlight: "AI-powered insights & forecasting!",
  },
  remedy: {
    icon: "fas fa-heartbeat",
    name: "REMEDY - Complaint Management",
    aiCharacter: "REMEDY",
    tagline: "Resolution & Mediation for Your Disputes",
    description: "Turn complaints into opportunities. REMEDY manages your customer complaint lifecycle with SLA monitoring, escalation workflows, and complete resolution tracking. Never let a customer issue slip through the cracks.",
    features: [
      "Complaint ticket system",
      "Multi-channel intake (email, phone, web)",
      "Automatic ticket assignment",
      "SLA monitoring & alerts",
      "Escalation workflows",
      "Priority-based queuing",
      "Customer communication log",
      "Resolution tracking & verification",
      "Root cause analysis",
      "Satisfaction surveys",
      "Complaint analytics & trends",
      "Agent performance metrics",
    ],
    highlight: "FREE for ALL users - even Visitor plan!",
    badge: "Free for All",
  },
  seal: {
    icon: "fas fa-stamp",
    name: "SEAL - E-Signature Module",
    aiCharacter: "SEAL",
    tagline: "Secure Electronic Agreement & Ledger",
    description: "SEAL handles all your digital signatures — quotes, contracts, NDAs, and agreements. Get documents signed faster, close deals quicker. Legally compliant under IT Act 2000 — available FREE to all users including Visitor plan.",
    features: [
      "E-signatures on quotes & proposals",
      "Contract signing workflow",
      "Multi-party signature support",
      "Document sharing & tracking",
      "Signature status notifications",
      "Automatic reminders",
      "Legal compliance (IT Act 2000)",
      "Audit trail for all signatures",
      "Template library",
      "Integration with CRM deals",
      "Signed document storage",
      "Expiry & renewal tracking",
    ],
    highlight: "FREE for ALL users - even Visitor plan!",
    badge: "Free for All",
  },
  tender: {
    icon: "fas fa-file-contract",
    name: "Tender Management",
    aiCharacter: "PROBE",
    tagline: "For Government Contractors",
    description: "Complete NIT extraction, CPWD rates database, BOQ-based tender quotes, and auto-project creation on winning. Built specifically for government contractors.",
    features: [
      "NIT extractor (67+ fields)",
      "CPWD DSR rate database",
      "BOQ-based tender quoting",
      "Rate analysis templates",
      "Tender submission tracking",
      "EMD/SD management",
      "Auto-project creation on winning",
      "RA Bill generation",
      "Measurement book entries",
      "Running account tracking",
      "Tender deadline alerts",
      "Historical rate comparison",
    ],
    highlight: "Specialty addon for govt contractors!",
    badge: "Addon",
  },
  "upi-payments": {
    icon: "fas fa-qrcode",
    name: "UPI Payment Collection",
    aiCharacter: "LEDGER",
    tagline: "Zero Gateway Fees",
    description: "Collect payments directly from customers via UPI with zero transaction fees. Every invoice auto-generates a BharatQR-compatible QR code. Customers scan with any UPI app — GPay, PhonePe, Paytm — and pay instantly. No Razorpay or Stripe middleman fees.",
    features: [
      "Auto-generated BharatQR code on every invoice",
      "One-tap deep links for GPay, PhonePe, Paytm",
      "Public payment page (no customer login needed)",
      "UTR-based payment confirmation",
      "Duplicate UTR detection (anti-fraud)",
      "Invoice auto-updates on payment",
      "Configure your own UPI ID (business@upi)",
      "Multi-gateway support (UPI + Razorpay + Stripe)",
      "Works across all 19 industry verticals",
      "Payment history & reconciliation reports",
      "Secure — sensitive credentials never exposed",
      "Zero setup cost, zero transaction fees",
    ],
    highlight: "Zero fees — money goes straight to your UPI!",
    badge: "Free for All",
  },
  chorus: {
    icon: "fas fa-share-alt",
    name: "CHORUS - Social Media Studio",
    aiCharacter: "CHORUS",
    tagline: "Your Entire Social Media Team in One Dashboard",
    description: "CHORUS is a full-scale social media management suite — not just a scheduler. Post across 7 platforms including YouTube, manage Meta Ads and Google Ads, run WhatsApp campaigns, build AI auto-responders, sync your product catalog to Instagram Shopping, capture leads from DMs, and orchestrate AI agents that create content autonomously. 32 dedicated screens, 14,000+ lines of production code.",
    features: [
      "Multi-platform posting (Facebook, Instagram, LinkedIn, Twitter/X, Threads, YouTube)",
      "YouTube Studio — upload with title, tags, category, thumbnail and privacy set before publish",
      "YouTube comments — read, reply, moderate and delete in the same inbox as your other replies",
      "Google Business Profile — manage the listing customers find when they search for you",
      "Unified Messenger inbox across all Meta platforms",
      "Content calendar with drag-and-drop scheduling",
      "Meta Ads dashboard — create, optimize & track campaigns",
      "Google Ads — campaigns, keywords, budgets, bids and conversion tracking",
      "Ads Insights — campaign performance analytics",
      "Custom & Lookalike Audience builder",
      "WhatsApp Business templates — bulk messaging with Meta approval",
      "AI Auto-Responses with sentiment analysis",
      "Instagram Shopping — shoppable posts & catalog sync",
      "Lead Forms builder for social campaigns",
      "Fundraiser management for nonprofits",
      "Live streaming management",
      "Agent OS — AI agent orchestration (strategic, creation, distribution layers)",
      "Knowledge Brain — custom RAG knowledge base for AI",
      "Voice Studio — AI voice content generation",
      "Podcast Studio — audio content creation",
      "Engagement analytics per platform with trend tracking",
      "Lead detection — AI routes DMs to BARTER CRM automatically",
      "Complaint detection — routes negative sentiment to REMEDY",
    ],
    highlight: "32 screens — the most comprehensive social suite in any ERP!",
    interconnections: [
      { module: "CRM & Sales", slug: "crm", description: "Leads detected from social DMs auto-create in BARTER pipeline" },
      { module: "REMEDY Complaints", slug: "remedy", description: "Negative sentiment in messages auto-routes to complaint tickets" },
      { module: "Storefront", slug: "storefront", description: "Product catalog syncs to Instagram Shopping and Facebook Shop" },
      { module: "Email Suite", slug: "email", description: "Social leads receive automated nurture email sequences" },
    ],
    // YouTube and Google Business Profile are Google grants as well as CHORUS
    // channels, so the Google hub is the page that documents their permissions.
    externalLink: { to: "/feature/google-marketing", label: "See the Google Marketing module" },
  },
  // ── Google ──
  // Split productivity from marketing because they are granted separately and
  // sold to different people in the same business: the office manager wants
  // Gmail and Drive, the person who owns the ad budget wants Ads and Analytics.
  // Scope strings are deliberately named here — they are the same values on
  // Google's consent screen and in section 6 of the privacy policy, and a
  // feature list that overstates a grant contradicts a legal document.
  "google-workspace": {
    icon: "fab fa-google",
    name: "Google Workspace",
    aiCharacter: "ECHO",
    tagline: "Gmail, Calendar, Drive, Docs & Sheets — Inside Your Dashboard",
    description:
      "Connect your Google account once and Workspace stops being a separate tab. Your mail lands in the AXOIX inbox, your bookings write themselves into your calendar, your contracts attach to the deal they belong to, and every report exports as a live Google Sheet. Each permission is approved separately on Google's own consent screen, and you can withdraw any of them at any time.",
    features: [
      "Gmail in the AXOIX inbox — read, send, reply, label, archive and download attachments",
      "Send-only Gmail option — quotes and invoices leave from your address, and AXOIX never reads your mail",
      "Two-way Google Calendar — your events show in AXOIX, AXOIX bookings write back into Google",
      "Bookings from every vertical sync: hotel check-ins, salon appointments, gym slots, class sessions, site visits",
      "Meeting invitations carry their video join link into the Google event",
      "Google Drive browser — search, rename, move, organise into folders, upload and download",
      "Attach any Drive file to the record it belongs to — a contract on the deal, a photo on the project",
      "Drive-limited option — AXOIX only ever sees files it created or that you explicitly hand it",
      "Generate real Google Docs — proposals, contracts, offer letters — pre-filled with your data",
      "Export any report or table straight to a live Google Sheet in your own Drive",
      "Google Classroom two-way sync for schools — courses, rosters, coursework, submissions and grades",
      "Sign in to AXOIX with Google",
    ],
    highlight: "One connection. Approve each permission separately, revoke any of them any time.",
    interconnections: [
      { module: "Email Suite", slug: "email", description: "Gmail becomes a mailbox inside the ECHO inbox alongside your AXOIX mail" },
      { module: "CRM & Sales", slug: "crm", description: "Mail threads and Drive contracts attach to the deal they belong to" },
      { module: "Customer Portal", slug: "customer-portal", description: "Bookings customers make land in your Google Calendar automatically" },
      { module: "Analytics", slug: "analytics", description: "Any VARN report exports to a live Google Sheet in one click" },
    ],
    externalLink: { to: "/privacy#section-6", label: "Exactly which Google permissions we request, and why" },
  },
  "google-marketing": {
    icon: "fas fa-bullhorn",
    name: "Google Marketing",
    aiCharacter: "CHORUS",
    tagline: "Google Ads, Analytics 4, YouTube & Business Profile",
    description:
      "Most businesses read ad spend in one tab and revenue in another. Your invoices, leads and deals already live in AXOIX — so Google Ads sits beside the money it produced. Full campaign management, not just a spend dashboard: create and manage Search and Display campaigns, ad groups, keywords, ads, budgets, bids, targeting and conversion actions.",
    features: [
      "Google Ads — create Search and Display campaigns with budget, bidding, locations and languages",
      "Bidding strategies including Target CPA, Target ROAS and Maximize Conversions",
      "Ad groups, keywords with match types, and negative keywords",
      "Responsive search ads and responsive display ads with an image asset library",
      "Search terms report — see what people actually typed, and block a term from the row",
      "Conversion actions for website and calls, with the tracking tag generated for you",
      "Keyword planner — keyword ideas from a seed term or a landing page",
      "Reporting at account, campaign, ad group, keyword and search-term level",
      "Google Analytics 4 — standard, pivot and realtime reports",
      "GA4 administration — properties, data streams, custom dimensions, and who can see the account",
      "YouTube — upload with title, tags, category, thumbnail and privacy set before publish",
      "YouTube comments — read, reply, moderate and delete in the same inbox as your other social replies",
      "Google Business Profile — manage the listing customers find when they search for you",
    ],
    highlight: "Writes to a live ad account require an Administrator role. Campaigns are created paused.",
    interconnections: [
      { module: "CHORUS Social", slug: "chorus", description: "YouTube and Business Profile connect as CHORUS channels alongside Meta and LinkedIn" },
      { module: "Accounting", slug: "accounting", description: "Ad spend is tracked against LEDGER expense accounts" },
      { module: "CRM & Sales", slug: "crm", description: "Campaign performance sits beside the leads and deals it produced" },
      { module: "Analytics", slug: "analytics", description: "GA4 and Ads metrics feed VARN dashboards next to your revenue" },
    ],
    externalLink: { to: "/privacy#section-6", label: "Exactly which Google permissions we request, and why" },
  },
  connect: {
    icon: "fas fa-comments",
    name: "CONNECT - Team Chat & Video",
    aiCharacter: "ECHO",
    tagline: "Slack + Zoom Built Into Your Dashboard",
    description: "CONNECT replaces Slack, Zoom, and Google Meet with a single integrated communication hub. Channels organized by department or project, direct messages, threaded conversations, voice huddles for quick syncs, and full Jitsi-powered video meetings with screen sharing — all inside AXOIX with zero extra subscriptions.",
    features: [
      "Organized channels by department, project, or topic",
      "Direct messages (1-on-1 and group)",
      "Threaded conversations for focused discussions",
      "File & document sharing with preview",
      "Jitsi-powered video meetings (no extra app or account needed)",
      "Screen sharing in video calls",
      "Voice huddles for quick stand-ups",
      "Real-time WebSocket messaging — instant delivery",
      "Message search across all channels & DMs",
      "@mentions with push notifications",
      "Pin important messages to channels",
      "Emoji reactions on messages",
      "Online/offline presence indicators",
      "Meeting invitations with calendar integration",
    ],
    highlight: "Replace Slack + Zoom — zero extra cost!",
    interconnections: [
      { module: "Project Management", slug: "projects", description: "Project channels auto-created when FORGE projects start" },
      { module: "LINK Phone", slug: "link", description: "Escalate chat to voice call with one click via LINK softphone" },
      { module: "HR & Workforce", slug: "hr", description: "Department channels auto-updated when employees join/leave" },
    ],
  },
  storefront: {
    icon: "fas fa-globe",
    name: "Website Builder & Storefront",
    aiCharacter: "FORGE",
    tagline: "Your Business Online in Minutes",
    description: "Every AXOIX vertical includes a public-facing storefront. Drag-and-drop section builder with multiple layout variants, custom domain support, and real-time sync with your dashboard data. No developers, no separate hosting.",
    features: [
      "Drag-and-drop section builder",
      "Multiple layout variants per section (Hero, About, Gallery, FAQ, Pricing, Team, Testimonials)",
      "Custom domain support (yourbusiness.com)",
      "Real-time sync with dashboard data",
      "Online booking integration (hotel, salon, gym)",
      "Online menu (restaurant)",
      "Property listings (real estate)",
      "Vehicle showcase (car & bike dealers)",
      "SEO tools built-in (meta, sitemap, robots.txt)",
      "Contact forms & WhatsApp chat widget",
      "Google Analytics & Pixel integration",
      "SSL certificate included",
    ],
    highlight: "No separate website needed!",
  },
  quotations: {
    icon: "fas fa-file-invoice",
    name: "Quotations & Proposals",
    aiCharacter: "BARTER",
    tagline: "Close Deals Faster with Professional Quotes",
    description: "BARTER's quotation engine turns your pricing into polished, branded proposals in seconds. Customers can accept online, e-sign via SEAL, and the deal moves straight to invoicing — no paperwork handoff.",
    features: [
      "Branded quote templates",
      "Product & service line items with tax calculation",
      "Discount tiers & volume pricing",
      "Multi-currency quotes",
      "Quote sharing via public link (no login for clients)",
      "One-click e-signature via SEAL",
      "Quote acceptance tracking & notifications",
      "Auto-convert accepted quote to invoice",
      "Quote expiry dates with reminders",
      "Version history & revision tracking",
      "PDF & print export",
      "Integrates with CRM opportunities",
    ],
    highlight: "Quote → E-Sign → Invoice in one workflow!",
  },
  "customer-portal": {
    icon: "fas fa-user-circle",
    name: "Customer Portal",
    aiCharacter: "ECHO",
    tagline: "Give Customers a Window into Your Business",
    description: "Every AXOIX vertical generates a dedicated customer portal — a branded interface where your clients can book appointments, track orders, view invoices, raise complaints, and pay — all without calling you.",
    features: [
      "Industry-specific customer portals (18 of 19 verticals)",
      "Online booking (hotel, salon, gym, healthcare)",
      "Order tracking (logistics, restaurant)",
      "Invoice viewing & UPI payment",
      "Complaint submission & status tracking via REMEDY",
      "Document sharing & e-signature requests via SEAL",
      "Appointment rescheduling",
      "Membership & subscription management (gym, salon)",
      "Property listings & inquiry (real estate)",
      "Tournament registration (arena gaming)",
      "Branded with your logo & colours",
      "Mobile-friendly responsive design",
    ],
    highlight: "14 industry portals — all included!",
    interconnections: [
      { module: "REMEDY Complaints", slug: "remedy", description: "Customers raise and track complaints from their portal" },
      { module: "SEAL E-Sign", slug: "seal", description: "Clients sign documents and contracts via portal" },
      { module: "UPI Payments", slug: "upi-payments", description: "Invoice payment via UPI directly from portal" },
    ],
  },
  costing: {
    icon: "fas fa-coins",
    name: "AI Costing Engine",
    aiCharacter: "FORGE",
    tagline: "Estimate Costs in Seconds — Not Days",
    description: "The AI Costing Engine accepts a project description in plain text, an uploaded image/floor plan, or a pre-built template — and generates a detailed cost breakdown with material, labour, and overhead line items. Supports multi-industry estimation across construction, manufacturing, events, interior design, and more.",
    features: [
      "Text-based estimation — describe the project, get itemized costs",
      "Image/diagram analysis — upload floor plans or site photos",
      "Template-based estimation — pick from industry templates",
      "Manual estimation with custom line items",
      "Material, labour & overhead cost breakdowns",
      "Margin & discount calculation",
      "Tax support (GST, custom rates)",
      "Material cost rates database",
      "Multi-industry support (construction, manufacturing, events, interiors)",
      "Floor plan generation from descriptions",
      "Template library with reusable estimates",
      "Export to PDF with company branding",
    ],
    highlight: "3 AI modes: Text, Image & Template estimation!",
    interconnections: [
      { module: "Inventory", slug: "inventory", description: "Material costs pulled from real-time inventory prices" },
      { module: "Quotations", slug: "quotations", description: "Cost estimates convert to client-facing quotes in one click" },
      { module: "Tender Management", slug: "tender", description: "Costing feeds into BOQ-based tender bids" },
      { module: "Accounting", slug: "accounting", description: "Approved estimates create budget entries in LEDGER" },
    ],
  },
  barter: {
    icon: "fas fa-store",
    name: "Barter Marketplace",
    aiCharacter: "BARTER",
    tagline: "Multi-Shop P2P Exchange & Commerce",
    description: "BARTER Marketplace lets tenants create multiple shops, list products or services, and participate in a peer-to-peer exchange network. Built-in referral system, order management, and seller analytics. Each shop gets its own dashboard with independent inventory tracking.",
    features: [
      "Multi-shop support per tenant",
      "Product & service listings with images",
      "Order management with status tracking",
      "Seller dashboard with analytics",
      "Auto-shop creation on signup",
      "Referral system with tracking",
      "Category management",
      "Search & discovery across marketplace",
      "Seller ratings & reviews",
      "Order notifications",
      "Revenue analytics per shop",
      "Integration with main inventory",
    ],
    highlight: "Run multiple shops from one dashboard!",
    interconnections: [
      { module: "Inventory", slug: "inventory", description: "Shop stock syncs with HAUL warehouse inventory" },
      { module: "Accounting", slug: "accounting", description: "Sales auto-post to LEDGER revenue accounts" },
      { module: "CRM & Sales", slug: "crm", description: "Marketplace buyers auto-added to BARTER CRM contacts" },
    ],
  },
};

const pageStyles = `
  .feature-detail-hero {
    background: linear-gradient(135deg, rgba(0,255,151,0.08), rgba(0,0,0,0));
    border-bottom: 1px solid rgba(0,255,151,0.15);
  }
  .feature-detail-hero-icon {
    width: 80px;
    height: 80px;
    border-radius: 20px;
    background: linear-gradient(135deg, rgba(0,255,151,0.2), rgba(0,255,151,0.05));
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 36px;
    color: var(--color-primary);
  }
  .feature-detail-badge {
    display: inline-block;
    font-size: 11px;
    font-weight: 700;
    padding: 5px 14px;
    border-radius: 14px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .feature-detail-badge--free {
    background: linear-gradient(135deg, var(--color-primary), #00cc79);
    color: var(--color-secondary);
  }
  .feature-detail-badge--addon {
    background: linear-gradient(135deg, #8b5cf6, #6d28d9);
    color: white;
  }
  .feature-detail-feature-card {
    background: rgba(15, 20, 40, 0.65);
    border: 1px solid rgba(0,255,151,0.15);
    border-radius: 16px;
    padding: 20px;
    height: 100%;
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;
  }
  .feature-detail-feature-card:hover {
    border-color: var(--color-primary);
    transform: translateY(-2px);
  }
  .feature-detail-feature-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: rgba(0,255,151,0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-primary);
    font-size: 14px;
    margin-bottom: 12px;
  }
  .feature-detail-highlight {
    display: inline-block;
    background: rgba(138,43,226,0.12);
    border: 1px solid rgba(138,43,226,0.3);
    border-radius: 12px;
    font-size: 14px;
    color: #a78bfa;
    padding: 12px 24px;
    backdrop-filter: blur(8px);
  }
  .feature-detail-split-title {
    color: var(--color-primary);
    font-size: 15px;
    font-weight: 600;
    margin-bottom: 12px;
  }
`;

const FeatureDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const config = slug ? MODULE_CONFIG[slug] : undefined;

  if (!config) {
    return <Navigate to="/404" replace />;
  }

  const hasFreePaid = config.freeFeatures && config.paidFeatures;

  const moduleSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: config.name,
    applicationCategory: "BusinessApplication",
    applicationSubCategory: config.aiCharacter,
    operatingSystem: "Web",
    description: config.description,
    url: `https://axoix.com/feature/${slug}`,
    image: "https://axoix.com/og-image.png",
    author: {
      "@type": "Organization",
      name: "AXOIX TECHNOLOGIES PRIVATE LIMITED",
      url: "https://axoix.com",
    },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
      availability: "https://schema.org/InStock",
      description: "Included in all AXOIX paid plans; some advanced features in Commander / Titan tiers.",
    },
    ...(config.freeFeatures && config.freeFeatures.length > 0
      ? { featureList: [...(config.features || []), ...config.freeFeatures, ...(config.paidFeatures || [])].slice(0, 15) }
      : config.features && config.features.length > 0
      ? { featureList: config.features.slice(0, 15) }
      : {}),
  };

  return (
    <Fragment>
      <SEO
        title={`${config.name} — Powered by ${config.aiCharacter}`}
        description={config.description}
        path={`/feature/${slug}`}
        schema={moduleSchema}
      />
      <style>{pageStyles}</style>
      <div className="about-page inner-page">
        <div className="body_wrap o-clip">
          <Header />
          <main
            style={{
              backgroundImage: `url(${serviceBg})`,
              backgroundSize: "cover",
              backgroundPosition: "center top",
              backgroundRepeat: "no-repeat",
              backgroundAttachment: "fixed",
            }}
          >
            <PageTitle
              pageTitle={config.name}
              pagesub="Features"
              headingLevel="h1"
              badges={
                <div className="d-flex gap-2 flex-wrap align-items-center justify-content-center">
                  <span className="opacity-75" style={{ fontSize: 14 }}>Powered by {config.aiCharacter}</span>
                  {config.badge && (
                    <span className={`feature-detail-badge ${config.badge === "Addon" ? "feature-detail-badge--addon" : "feature-detail-badge--free"}`}>
                      {config.badge}
                    </span>
                  )}
                </div>
              }
              description={
                <div>
                  {config.tagline && (
                    <p className="mb-10" style={{ color: "var(--color-primary)", fontWeight: 500, fontSize: "1.15rem" }}>
                      {config.tagline}
                    </p>
                  )}
                  <p className="mb-0 opacity-80" style={{ fontSize: "1.08rem", lineHeight: 1.7 }}>
                    {config.description}
                  </p>
                </div>
              }
              actions={
                <Link to="/signup" className="thm-btn agency-btn">
                  <span className="text">Get Started Free</span>
                  <span className="arrow">
                    <span className="arrow-icon">
                      <svg width="20" height="19" viewBox="0 0 20 19" fill="none"><rect x="3.783" y="13.463" width="14.31" height="1.817" transform="rotate(-40.2798 3.783 13.463)" fill="white"/><rect x="5.807" y="4.605" width="1.817" height="1.817" transform="rotate(-40.2798 5.807 4.605)" fill="white"/><rect x="8.367" y="4.816" width="1.817" height="1.817" transform="rotate(-40.2798 8.367 4.816)" fill="white"/><rect x="10.926" y="5.028" width="1.817" height="1.817" transform="rotate(-40.2798 10.926 5.028)" fill="white"/><rect x="13.277" y="7.8" width="1.817" height="1.817" transform="rotate(-40.2798 13.277 7.8)" fill="white"/><rect x="13.066" y="10.361" width="1.817" height="1.817" transform="rotate(-40.2798 13.066 10.361)" fill="white"/><rect x="12.855" y="12.922" width="1.817" height="1.817" transform="rotate(-40.2798 12.855 12.922)" fill="white"/></svg>
                      <svg width="20" height="19" viewBox="0 0 20 19" fill="none"><rect x="3.783" y="13.463" width="14.31" height="1.817" transform="rotate(-40.2798 3.783 13.463)" fill="white"/><rect x="5.807" y="4.605" width="1.817" height="1.817" transform="rotate(-40.2798 5.807 4.605)" fill="white"/><rect x="8.367" y="4.816" width="1.817" height="1.817" transform="rotate(-40.2798 8.367 4.816)" fill="white"/><rect x="10.926" y="5.028" width="1.817" height="1.817" transform="rotate(-40.2798 10.926 5.028)" fill="white"/><rect x="13.277" y="7.8" width="1.817" height="1.817" transform="rotate(-40.2798 13.277 7.8)" fill="white"/><rect x="13.066" y="10.361" width="1.817" height="1.817" transform="rotate(-40.2798 13.066 10.361)" fill="white"/><rect x="12.855" y="12.922" width="1.817" height="1.817" transform="rotate(-40.2798 12.855 12.922)" fill="white"/></svg>
                    </span>
                  </span>
                </Link>
              }
            />

            {/* Features */}
            <section className="pt-80 pb-60">
              <div className="container">
                <div className="text-center mb-50">
                  <span className="sub-title mb-15">Capabilities</span>
                  <h2 className="title">What You Get with {config.name}</h2>
                </div>

                {hasFreePaid ? (
                  <div className="row">
                    <div className="col-lg-6 mb-40">
                      <h5 className="feature-detail-split-title">
                        <i className="fas fa-check-circle me-2"></i>FREE Features
                      </h5>
                      <div className="row">
                        {config.freeFeatures!.map((f, idx) => (
                          <div key={idx} className="col-12 mb-15">
                            <div className="feature-detail-feature-card">
                              <div className="feature-detail-feature-icon">
                                <i className="fas fa-check"></i>
                              </div>
                              <p className="mb-0 opacity-85" style={{ fontSize: 14 }}>{f}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="col-lg-6 mb-40">
                      <h5 className="feature-detail-split-title">
                        <i className="fas fa-star me-2"></i>Premium Features
                      </h5>
                      <div className="row">
                        {config.paidFeatures!.map((f, idx) => (
                          <div key={idx} className="col-12 mb-15">
                            <div className="feature-detail-feature-card">
                              <div className="feature-detail-feature-icon">
                                <i className="fas fa-star" style={{ color: "#a78bfa" }}></i>
                              </div>
                              <p className="mb-0 opacity-85" style={{ fontSize: 14 }}>{f}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="row">
                    {config.features.map((f, idx) => (
                      <div key={idx} className="col-lg-4 col-md-6 mb-20">
                        <div className="feature-detail-feature-card">
                          <div className="feature-detail-feature-icon">
                            <i className="fas fa-check"></i>
                          </div>
                          <p className="mb-0 opacity-85">{f}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Highlight */}
                <div className="text-center mt-30">
                  <span className="feature-detail-highlight">
                    <i className="fas fa-bolt me-2"></i>
                    {config.highlight}
                  </span>
                </div>
              </div>
            </section>

            {/* Interconnections */}
            {config.interconnections && config.interconnections.length > 0 && (
              <section className="pt-60 pb-60">
                <div className="container">
                  <div className="text-center mb-40">
                    <span className="sub-title mb-15">Module Interconnections</span>
                    <h2 className="title">How {config.name} Connects</h2>
                    <p className="opacity-75 mt-10" style={{ maxWidth: 600, margin: "10px auto 0" }}>
                      Every AXOIX module talks to every other. Here's how {config.name} integrates with the rest of your dashboard.
                    </p>
                  </div>
                  <div className="row justify-content-center">
                    {config.interconnections.map((ic, idx) => (
                      <div key={idx} className="col-lg-6 col-md-6 mb-20">
                        <Link to={`/feature/${ic.slug}`} className="feature-detail-feature-card d-block text-decoration-none" style={{ color: "inherit" }}>
                          <div className="d-flex align-items-start gap-3">
                            <div className="feature-detail-feature-icon" style={{ flexShrink: 0 }}>
                              <i className="fas fa-link"></i>
                            </div>
                            <div>
                              <h6 className="mb-5" style={{ color: "var(--color-primary)", fontSize: 14 }}>{ic.module}</h6>
                              <p className="mb-0 opacity-75" style={{ fontSize: 13 }}>{ic.description}</p>
                            </div>
                          </div>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Connections outside AXOIX. Only the modules that actually hold a
                third-party grant set this, so most pages render nothing. */}
            {config.externalLink && (
              <section className="pt-0 pb-40">
                <div className="container text-center">
                  <Link
                    to={config.externalLink.to}
                    className="thm-btn agency-btn"
                    style={{ background: "transparent", border: "1px solid var(--color-primary)" }}
                  >
                    <span className="text">{config.externalLink.label}</span>
                  </Link>
                </div>
              </section>
            )}

            {/* CTA */}
            <section className="pt-80 pb-100">
              <div className="container text-center">
                <div className="pricing-item xb-border bg_img p-50" style={{ backgroundImage: `url(${cardBg})`, borderRadius: 20 }}>
                  <h2 className="mb-15">Ready to Use {config.name}?</h2>
                  <p className="opacity-75 mb-30" style={{ maxWidth: 600, margin: "0 auto 30px" }}>
                    Sign up for the free Explorer plan today. No credit card required. Upgrade anytime as your business grows.
                  </p>
                  <div className="d-flex gap-3 justify-content-center flex-wrap">
                    <Link to="/signup" className="thm-btn agency-btn">
                      <span className="text">Get Started Free</span>
                    </Link>
                    <Link to="/features" className="thm-btn agency-btn" style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.2)" }}>
                      <span className="text">View All Modules</span>
                    </Link>
                  </div>
                </div>
              </div>
            </section>

          </main>
          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default FeatureDetailPage;
